import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import timeTrackingService from '../services/timeTrackingService';
import TimeTrackingNav from '../components/TimeTrackingNav';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const EMPTY_POLICY = { name: '', type: 'pto', carryoverMaxHours: '', carryoverExpiryMonths: '', notes: '' };

// ── Policy Card ───────────────────────────────────────────────────────────────

function PolicyCard({ policy, onEdit, onDelete }) {
  const tiers = [...(policy.Tiers || [])].sort((a, b) => a.minYears - b.minYears);
  const isPto = policy.type === 'pto';
  const accentColor = isPto ? '#2563eb' : '#16a34a';

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderLeft: `4px solid ${accentColor}`,
      borderRadius: 10,
      padding: '16px 18px',
      marginBottom: 10,
    }}>
      {/* Header row */}
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="d-flex align-items-center gap-2">
          <span
            className="badge"
            style={{ background: isPto ? '#dbeafe' : '#dcfce7', color: isPto ? '#1d4ed8' : '#15803d', fontWeight: 600, fontSize: 11 }}
          >
            {isPto ? 'PTO' : 'Sick'}
          </span>
          <span className="fw-semibold" style={{ fontSize: 15 }}>{policy.name}</span>
        </div>
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-light"
            style={{ fontSize: 12, padding: '3px 10px', border: '1px solid #e5e7eb' }}
            onClick={() => onEdit(policy)}
          >
            Edit
          </button>
          <button
            className="btn btn-sm btn-light text-danger"
            style={{ fontSize: 12, padding: '3px 10px', border: '1px solid #fecaca' }}
            onClick={() => onDelete(policy)}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Tier progression */}
      {tiers.length > 0 && (
        <div className="d-flex align-items-center gap-2 mt-3 flex-wrap">
          {tiers.map((t, i) => (
            <React.Fragment key={i}>
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '6px 12px',
                textAlign: 'center',
                minWidth: 70,
              }}>
                <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t.minYears === 0 ? 'Start' : `${t.minYears}+ yr`}
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: accentColor, lineHeight: 1.3 }}>
                  {t.hoursPerYear}h
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>per year</div>
              </div>
              {i < tiers.length - 1 && (
                <span style={{ color: '#cbd5e1', fontSize: 18 }}>→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Carryover + notes */}
      <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
        {policy.carryoverMaxHours != null ? (
          <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', borderRadius: 20, padding: '2px 10px', border: '1px solid #e2e8f0' }}>
            ↩ Carryover: up to {policy.carryoverMaxHours}h
            {policy.carryoverExpiryMonths != null
              ? ` · expires ${policy.carryoverExpiryMonths} month${policy.carryoverExpiryMonths > 1 ? 's' : ''}`
              : ' · no expiry'}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: '#94a3b8' }}>No carryover</span>
        )}
        {policy.notes && (
          <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>{policy.notes}</span>
        )}
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptySection({ type, onNew }) {
  return (
    <div
      style={{
        border: '2px dashed #e2e8f0',
        borderRadius: 10,
        padding: '28px 20px',
        textAlign: 'center',
        color: '#94a3b8',
      }}
    >
      <div style={{ fontSize: 32, marginBottom: 8 }}>{type === 'pto' ? '🏖️' : '🏥'}</div>
      <div style={{ fontWeight: 500, marginBottom: 4 }}>No {type === 'pto' ? 'PTO' : 'Sick'} policies yet</div>
      <div style={{ fontSize: 13, marginBottom: 14 }}>Create a policy to assign to employees</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onNew}>+ New Policy</button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function TimeOffPoliciesPage() {
  const [policies,  setPolicies]  = useState([]);
  const [settings,  setSettings]  = useState({ fiscalYearEndMonth: 12, fiscalYearEndDay: 31 });
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  const [modal,     setModal]     = useState(null);
  const [form,      setForm]      = useState(EMPTY_POLICY);
  const [tiers,     setTiers]     = useState([{ minYears: 0, hoursPerYear: '' }]);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting,      setDeleting]      = useState(false);

  const [fyForm,   setFyForm]   = useState({ fiscalYearEndMonth: 12, fiscalYearEndDay: 31 });
  const [fySaving, setFySaving] = useState(false);
  const [fyMsg,    setFyMsg]    = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        timeTrackingService.getTimeOffPolicies(),
        timeTrackingService.getTimeOffSettings(),
      ]);
      setPolicies(p);
      setSettings(s);
      setFyForm({ fiscalYearEndMonth: s.fiscalYearEndMonth, fiscalYearEndDay: s.fiscalYearEndDay });
    } catch {
      setError('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = (type = 'pto') => {
    setForm({ ...EMPTY_POLICY, type });
    setTiers([{ minYears: 0, hoursPerYear: '' }]);
    setSaveError('');
    setModal({ mode: 'new' });
  };

  const openEdit = (p) => {
    setForm({
      name: p.name, type: p.type,
      carryoverMaxHours:     p.carryoverMaxHours     ?? '',
      carryoverExpiryMonths: p.carryoverExpiryMonths ?? '',
      notes: p.notes || '',
    });
    setTiers(
      p.Tiers?.length
        ? [...p.Tiers].sort((a, b) => a.minYears - b.minYears).map(t => ({ minYears: t.minYears, hoursPerYear: t.hoursPerYear }))
        : [{ minYears: 0, hoursPerYear: '' }]
    );
    setSaveError('');
    setModal({ mode: 'edit', policy: p });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return setSaveError('Policy name is required.');
    if (!tiers.length || tiers.some(t => t.minYears === '' || t.hoursPerYear === '')) {
      return setSaveError('All tier rows must have min years and hours filled in.');
    }
    setSaving(true); setSaveError('');
    try {
      const payload = {
        ...form,
        carryoverMaxHours:     form.carryoverMaxHours     !== '' ? Number(form.carryoverMaxHours)     : null,
        carryoverExpiryMonths: form.carryoverExpiryMonths !== '' ? Number(form.carryoverExpiryMonths) : null,
        tiers: tiers.map(t => ({ minYears: Number(t.minYears), hoursPerYear: Number(t.hoursPerYear) })),
      };
      if (modal.mode === 'new') {
        const p = await timeTrackingService.createTimeOffPolicy(payload);
        setPolicies(prev => [...prev, p].sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name)));
      } else {
        const p = await timeTrackingService.updateTimeOffPolicy(modal.policy.id, payload);
        setPolicies(prev => prev.map(x => x.id === p.id ? p : x));
      }
      setModal(null);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await timeTrackingService.deleteTimeOffPolicy(confirmDelete.id);
      setPolicies(prev => prev.filter(p => p.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete policy');
    } finally {
      setDeleting(false);
    }
  };

  const saveFiscalYear = async () => {
    setFySaving(true); setFyMsg('');
    try {
      const s = await timeTrackingService.saveTimeOffSettings(fyForm);
      setSettings(s);
      setFyMsg('Saved!');
      setTimeout(() => setFyMsg(''), 3000);
    } catch {
      setFyMsg('Failed to save');
    } finally {
      setFySaving(false);
    }
  };

  const addTier    = () => setTiers(prev => [...prev, { minYears: '', hoursPerYear: '' }]);
  const removeTier = (i) => setTiers(prev => prev.filter((_, idx) => idx !== i));
  const updateTier = (i, field, val) =>
    setTiers(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));

  // Returns the number of days in a given 1-based month (non-leap year for FY purposes)
  const daysInMonth = (month) => new Date(2001, month, 0).getDate();

  const handleFyMonthChange = (month) => {
    const maxDay = daysInMonth(Number(month));
    setFyForm(f => ({
      fiscalYearEndMonth: Number(month),
      fiscalYearEndDay: Math.min(f.fiscalYearEndDay, maxDay),
    }));
  };

  const handleFyDayChange = (day) => {
    const maxDay = daysInMonth(fyForm.fiscalYearEndMonth);
    const clamped = Math.max(1, Math.min(Number(day), maxDay));
    setFyForm(f => ({ ...f, fiscalYearEndDay: clamped }));
  };

  const ptoPolicies  = policies.filter(p => p.type === 'pto');
  const sickPolicies = policies.filter(p => p.type === 'sick');
  const fyLabel      = `${MONTHS[(settings.fiscalYearEndMonth ?? 12) - 1]} ${settings.fiscalYearEndDay ?? 31}`;

  return (
    <div className="container mt-4">
      <TimeTrackingNav />

      {/* Page toolbar */}
      <div className="d-flex align-items-center gap-2 mt-3 mb-4 flex-wrap">
        <h5 className="mb-0 me-2">Time Off Policies</h5>
        <button className="btn btn-sm btn-primary"         onClick={() => openNew('pto')}>+ PTO Policy</button>
        <button className="btn btn-sm btn-outline-success" onClick={() => openNew('sick')}>+ Sick Policy</button>
        <Link to="/time-tracking/time-off" className="btn btn-sm btn-outline-secondary ms-auto">
          ← Dashboard
        </Link>
      </div>

      {loading && <div className="text-muted py-3 text-center">Loading…</div>}
      {error   && <div className="alert alert-danger">{error}</div>}

      {!loading && (
        <>
          {/* Two-column layout */}
          <div className="row g-4">
            {/* PTO column */}
            <div className="col-md-6">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#2563eb', display: 'inline-block' }} />
                  <span className="fw-semibold text-uppercase" style={{ fontSize: 12, letterSpacing: '0.08em', color: '#475569' }}>PTO Policies</span>
                  <span className="badge bg-light text-secondary border">{ptoPolicies.length}</span>
                </div>
                <button className="btn btn-sm btn-light" style={{ fontSize: 12, border: '1px solid #e2e8f0' }} onClick={() => openNew('pto')}>+ New</button>
              </div>
              {ptoPolicies.length === 0
                ? <EmptySection type="pto" onNew={() => openNew('pto')} />
                : ptoPolicies.map(p => <PolicyCard key={p.id} policy={p} onEdit={openEdit} onDelete={setConfirmDelete} />)
              }
            </div>

            {/* Sick column */}
            <div className="col-md-6">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                  <span className="fw-semibold text-uppercase" style={{ fontSize: 12, letterSpacing: '0.08em', color: '#475569' }}>Sick Policies</span>
                  <span className="badge bg-light text-secondary border">{sickPolicies.length}</span>
                </div>
                <button className="btn btn-sm btn-light" style={{ fontSize: 12, border: '1px solid #e2e8f0' }} onClick={() => openNew('sick')}>+ New</button>
              </div>
              {sickPolicies.length === 0
                ? <EmptySection type="sick" onNew={() => openNew('sick')} />
                : sickPolicies.map(p => <PolicyCard key={p.id} policy={p} onEdit={openEdit} onDelete={setConfirmDelete} />)
              }
            </div>
          </div>

          {/* Fiscal Year Settings */}
          <div className="mt-4 pt-3" style={{ borderTop: '1px solid #f1f5f9' }}>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>📅 Fiscal Year Ends:</span>
                <span className="fw-semibold" style={{ fontSize: 13 }}>{fyLabel}</span>
              </div>
              <select
                className="form-select form-select-sm"
                style={{ width: 140 }}
                value={fyForm.fiscalYearEndMonth}
                onChange={e => handleFyMonthChange(e.target.value)}
              >
                {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <input
                type="number" min="1" max={daysInMonth(fyForm.fiscalYearEndMonth)}
                className="form-control form-control-sm"
                style={{ width: 72 }}
                value={fyForm.fiscalYearEndDay}
                onChange={e => handleFyDayChange(e.target.value)}
              />
              <button className="btn btn-sm btn-outline-primary" onClick={saveFiscalYear} disabled={fySaving}>
                {fySaving ? 'Saving…' : 'Update'}
              </button>
              {fyMsg && <span className="small text-success">{fyMsg}</span>}
            </div>
          </div>
        </>
      )}

      {/* ── Policy modal ─────────────────────────────────────────────────────── */}
      {modal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modal.mode === 'new'
                    ? `New ${form.type === 'pto' ? 'PTO' : 'Sick'} Policy`
                    : `Edit — ${modal.policy.name}`}
                </h5>
                <button className="btn-close" onClick={() => setModal(null)} />
              </div>
              <div className="modal-body">
                {saveError && <div className="alert alert-danger py-2 small">{saveError}</div>}
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label small fw-semibold">Policy Name</label>
                    <input
                      className="form-control"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Standard PTO"
                      autoFocus
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Type</label>
                    <select
                      className="form-select"
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                      disabled={modal.mode === 'edit'}
                    >
                      <option value="pto">PTO</option>
                      <option value="sick">Sick</option>
                    </select>
                    {modal.mode === 'edit' && <div className="form-text">Type cannot be changed.</div>}
                  </div>

                  {/* Tiers */}
                  <div className="col-12">
                    <label className="form-label small fw-semibold">Hours per Year by Tenure</label>
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0' }}>
                      {tiers.map((t, i) => (
                        <div key={i} className="d-flex align-items-center gap-2 mb-2">
                          <div style={{ width: 130 }}>
                            <div className="input-group input-group-sm">
                              <input
                                type="number" min="0"
                                className="form-control"
                                value={t.minYears}
                                onChange={e => updateTier(i, 'minYears', e.target.value)}
                                placeholder="0"
                              />
                              <span className="input-group-text">yr+</span>
                            </div>
                          </div>
                          <span className="text-muted">→</span>
                          <div style={{ width: 130 }}>
                            <div className="input-group input-group-sm">
                              <input
                                type="number" min="0" step="0.5"
                                className="form-control"
                                value={t.hoursPerYear}
                                onChange={e => updateTier(i, 'hoursPerYear', e.target.value)}
                                placeholder="e.g. 40"
                              />
                              <span className="input-group-text">h/yr</span>
                            </div>
                          </div>
                          {tiers.length > 1 && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              style={{ padding: '2px 8px' }}
                              onClick={() => removeTier(i)}
                            >×</button>
                          )}
                        </div>
                      ))}
                      <button className="btn btn-sm btn-outline-secondary mt-1" onClick={addTier}>
                        + Add Tier
                      </button>
                    </div>
                  </div>

                  {/* Carryover */}
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">
                      Max Carryover Hours
                      <span className="fw-normal text-muted ms-1">(blank = no carryover)</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="number" min="0" step="0.5"
                        className="form-control"
                        value={form.carryoverMaxHours}
                        onChange={e => setForm(f => ({ ...f, carryoverMaxHours: e.target.value }))}
                        placeholder="e.g. 40"
                      />
                      <span className="input-group-text">h</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">
                      Carryover Expires After
                      <span className="fw-normal text-muted ms-1">(blank = never)</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="number" min="1" max="12"
                        className="form-control"
                        value={form.carryoverExpiryMonths}
                        onChange={e => setForm(f => ({ ...f, carryoverExpiryMonths: e.target.value }))}
                        placeholder="e.g. 3"
                        disabled={!form.carryoverMaxHours}
                      />
                      <span className="input-group-text">months</span>
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label small fw-semibold">
                      Notes <span className="fw-normal text-muted">(optional)</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Policy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ────────────────────────────────────────────────────── */}
      {confirmDelete && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center py-4">
                <div style={{ fontSize: '2rem' }}>🗑️</div>
                <div className="fw-semibold mt-2">Delete "{confirmDelete.name}"?</div>
                <div className="text-muted small mt-1">Policy must have no employees assigned.</div>
              </div>
              <div className="modal-footer justify-content-center">
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
